import { SOC_CUSTOMER_FAQ } from '../constants/faqData';
import {
  aiReset,
  aiTurn,
  canCancelStatus,
  cancelUserOrder,
  defaultSchedule,
  formatAddressList,
  formatOrderList,
  formatOrderStatus,
  formatSchedule,
  hasScheduleHint,
  isGenericCatalogQuery,
  loadOrderDetail,
  loadLatestCompletedOrderPendingReview,
  loadUserAddresses,
  loadUserOrders,
  mapPaymentChoiceToCartMethod,
  parseBookingIntent,
  parseScheduleInput,
  parseSelectionIndex,
  parseYesNo,
  paymentPrompt,
  pickAddressFromHint,
  pickBestServiceMatch,
  sanitizeUserFacingText,
  searchServices,
  serviceMatchesQuery,
  stripBookingNoise,
  submitCustomerOrderFeedback,
  trackRichForOrder,
  wrapTurn,
} from './supportAssistantData';
import { parseSocIntent, type SocIntent } from './supportAssistantIntent';
import { langKeyFromCode, t, type LangKey } from './supportAssistantLocale';
import {
  COMPLAINT_REASONS,
  suggestionsForFlow,
  timeOfDayGreeting,
} from './socChatUiConfig';
import { expandSearchTokens, normalizeSearchText } from '../utils/serviceSearchUtils';
import {
  EMPTY_SESSION,
  type AssistantContext,
  type AssistantMessage,
  type AssistantReply,
  type AssistantSession,
  type AssistantTurnResult,
  type BookingPaymentChoice,
  type ChatAction,
  type CheckoutPayload,
  type OrderPick,
  type RichBlock,
  type ServicePick,
} from './supportAssistantTypes';

const SUPPORT_EMAIL = 'support@soc.com';
const SERVICE_PAGE_SIZE = 8;

function servicePriceHint(price: number): string | undefined {
  return price > 0 ? `From Rs. ${price}` : undefined;
}

function parsePickIndex(text: string, kind: 'service' | 'address' | 'order'): number | null {
  const match = text.trim().match(new RegExp(`^pick:${kind}:(\\d+)$`));
  if (!match) return null;
  const idx = Number(match[1]);
  return Number.isFinite(idx) && idx >= 0 ? idx : null;
}

function parseRating(text: string): number | null {
  const direct = text.trim().match(/^(?:review:rating:|rating:)?([1-5])$/i);
  if (direct) return Number(direct[1]);
  const stars = text.match(/\b([1-5])\s*(?:star|stars|rating)?\b/i);
  return stars ? Number(stars[1]) : null;
}

function parseDirectReview(text: string): { orderId: string; rating: number } | null {
  const match = text.trim().match(/^review:order:([^:]+):rating:([1-5])$/i);
  if (!match) return null;
  return { orderId: match[1], rating: Number(match[2]) };
}

function ratingActions(orderId?: string): ChatAction[] {
  return [5, 4, 3, 2, 1].map((rating) => ({
    type: 'prompt' as const,
    label: `${rating} star${rating === 1 ? '' : 's'}`,
    message: orderId ? `review:order:${orderId}:rating:${rating}` : `review:rating:${rating}`,
    style: rating >= 4 ? 'primary' as const : 'chip' as const,
  }));
}

function servicePickActions(services: ServicePick[], start: number): ChatAction[] {
  return services.map((service, i) => ({
    type: 'prompt' as const,
    label: service.name,
    description: servicePriceHint(service.price),
    style: 'card' as const,
    message: `pick:service:${start + i}`,
  }));
}

function hasAnyIndicIntent(text: string, words: string[]): boolean {
  const hay = text.toLocaleLowerCase('en-IN').normalize('NFC');
  return words.some((word) => hay.includes(word.toLocaleLowerCase('en-IN').normalize('NFC')));
}

const KN = {
  hello: '\u0ca8\u0cae\u0cb8\u0ccd\u0c95\u0cbe\u0cb0',
  menu: '\u0cae\u0cc6\u0ca8\u0cc1',
  startAgain: '\u0cae\u0ca4\u0ccd\u0ca4\u0cc6 \u0cb6\u0cc1\u0cb0\u0cc1',
  thanks: '\u0ca7\u0ca8\u0ccd\u0caf\u0cb5\u0cbe\u0ca6',
  book: '\u0cac\u0cc1\u0c95\u0ccd',
  booking: '\u0cac\u0cc1\u0c95\u0ccd\u0c95\u0cbf\u0c82\u0c97\u0ccd',
  service: '\u0cb8\u0cc7\u0cb5\u0cc6',
  services: '\u0cb8\u0cc7\u0cb5\u0cc6\u0c97\u0cb3\u0cc1',
  make: '\u0cae\u0cbe\u0ca1\u0cbf',
  need: '\u0cac\u0cc7\u0c95\u0cc1',
  status: '\u0cb8\u0ccd\u0ca5\u0cbf\u0ca4\u0cbf',
  order: '\u0c86\u0cb0\u0ccd\u0ca1\u0cb0\u0ccd',
  track: '\u0c9f\u0ccd\u0cb0\u0ccd\u0caf\u0cbe\u0c95\u0ccd',
  where: '\u0c8e\u0cb2\u0ccd\u0cb2\u0cbf',
  cancel: '\u0cb0\u0ca6\u0ccd\u0ca6\u0cc1',
  cancel2: '\u0c95\u0ccd\u0caf\u0cbe\u0ca8\u0ccd\u0cb8\u0cb2\u0ccd',
  address: '\u0cb5\u0cbf\u0cb3\u0cbe\u0cb8',
  address2: '\u0c85\u0ca1\u0ccd\u0cb0\u0cc6\u0cb8\u0ccd',
  price: '\u0cac\u0cc6\u0cb2\u0cc6',
  rate: '\u0ca6\u0cb0',
  howMuch: '\u0c8e\u0cb7\u0ccd\u0c9f\u0cc1',
  cost: '\u0c96\u0cb0\u0ccd\u0c9a\u0cc1',
  support: '\u0cb8\u0cb9\u0cbe\u0caf',
  human: '\u0cae\u0cbe\u0ca8\u0cb5',
  agent: '\u0c8f\u0c9c\u0cc6\u0c82\u0c9f\u0ccd',
  payment: '\u0caa\u0cbe\u0cb5\u0ca4\u0cbf',
  money: '\u0cb9\u0ca3',
  refund: '\u0cb0\u0cbf\u0cab\u0c82\u0ca1\u0ccd',
  complaint: '\u0ca6\u0cc2\u0cb0\u0cc1',
  late: '\u0ca4\u0ca1',
  problem: '\u0cb8\u0cae\u0cb8\u0ccd\u0caf\u0cc6',
  damage: '\u0cb9\u0cbe\u0ca8\u0cbf',
};

const RESET_INTENTS = ['reset', 'start over', 'cancel flow', 'never mind', 'nevermind', 'main menu', 'menu', KN.menu, KN.startAgain, '\u0cb9\u0cca\u0cb8\u0ca6\u0cc1', '\u0c86\u0cb0\u0c82\u0cad'];
const GREETING_INTENTS = ['hi', 'hello', 'hey', 'namaste', 'heli', 'helu', 'helide', 'halo', 'hallo', 'namaskara', 'namaskaram', KN.hello, '\u0cb9\u0cb2\u0ccb', '\u0cb9\u0cc6\u0cb2\u0ccd\u0cb2\u0cbf'];
const THANKS_INTENTS = ['thanks', 'thank you', 'bye', KN.thanks, '\u0cb8\u0cbe\u0c95\u0cc1', '\u0cae\u0cc1\u0c97\u0cbf\u0ca4\u0cc1'];

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .normalize('NFC')
    .replace(/[^a-z0-9\u0900-\u097F\u0C80-\u0CFF\u0B80-\u0BFF\u0C00-\u0C7F\s]/gi, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 1);
}

function scoreFaq(query: string): { question: string; answer: string } | null {
  const tokens = tokenize(query);
  if (tokens.length === 0) return null;
  let best: { question: string; answer: string; score: number } | null = null;
  for (const item of SOC_CUSTOMER_FAQ) {
    const hay = `${item.question} ${item.answer}`.toLowerCase();
    let score = 0;
    for (const t of tokens) if (hay.includes(t)) score += t.length > 5 ? 3 : 1;
    if (!best || score > best.score) best = { ...item, score };
  }
  return best && best.score >= 2 ? { question: best.question, answer: best.answer } : null;
}

function promptActions(labels: string[], messages: string[]): ChatAction[] {
  return labels.map((label, i) => ({ type: 'prompt' as const, label, message: messages[i] ?? label }));
}

function requireUserId(ctx: AssistantContext): string | null {
  return ctx.userId?.trim() || null;
}

function resetReply(content: string, actions?: ChatAction[]): AssistantTurnResult {
  return aiReset(sanitizeUserFacingText(content), actions);
}

function trackOrderReply(
  order: OrderPick,
  intro: string,
  actions?: ChatAction[],
  sessionExtras?: Partial<AssistantSession>,
): AssistantTurnResult {
  return {
    reply: {
      content: intro,
      actions,
      rich: trackRichForOrder(order),
      suggestions: ['📍 Track Booking', '📞 Support', '🏠 Book Now'],
      source: 'api',
    },
    session: { ...EMPTY_SESSION, ...sessionExtras },
  };
}

function bookingRichSummary(session: AssistantSession): RichBlock[] {
  if (!session.pickedService || !session.pickedAddress || !session.scheduledAt) return [];
  const schedule = new Date(session.scheduledAt);
  const rows: { label: string; value: string; highlight?: boolean }[] = [
    { label: 'Service', value: session.pickedService.name },
    { label: 'Address', value: `${session.pickedAddress.label} · ${session.pickedAddress.city || session.pickedAddress.line}`.slice(0, 48) },
    { label: 'Date', value: formatSchedule(schedule, session.preferredTime ?? '10:00 AM').split('·')[0]?.trim() || 'Scheduled' },
    { label: 'Time', value: session.preferredTime ?? '10:00 AM' },
  ];
  if (session.pickedService.price > 0) {
    rows.push({ label: 'Estimated Price', value: `From Rs. ${session.pickedService.price}`, highlight: true });
  }
  return [{ kind: 'kv', rows }];
}

function buildPaymentStepReply(
  session: AssistantSession,
  ctx: AssistantContext,
  headline?: string,
): AssistantTurnResult {
  const lang = languagePrefix(ctx);
  const summary = bookingSummaryLines(session);
  const intro = headline ?? t(lang, 'bookingReady');
  const actions = [
    checkoutAction({ ...session, paymentChoice: 'online_full' }, t(lang, 'payOnline')),
    checkoutAction({ ...session, paymentChoice: 'online_partial' }, t(lang, 'partialPay')),
    checkoutAction({ ...session, paymentChoice: 'cash_on_service' }, t(lang, 'cashOnService')),
  ].filter(Boolean) as ChatAction[];
  return aiTurn(
    `${intro}\n\n${summary}\n\n${paymentPrompt(ctx)}`,
    { ...session, flow: 'book_pick_payment' },
    actions,
    { rich: bookingRichSummary(session), suggestions: suggestionsForFlow('book_pick_payment') },
  );
}

function mainMenuActions(): ChatAction[] {
  return promptActions(
    ['Book service', 'Track orders', 'Price estimate', 'My addresses', 'Support'],
    ['book a service', 'track my orders', 'cleaning price', 'my addresses', 'talk to support']
  );
}

function languagePrefix(ctx: AssistantContext): LangKey {
  return langKeyFromCode(ctx.languageCode);
}

function inferLanguageFromText(text: string, fallback?: string): LangKey {
  const lower = text.toLocaleLowerCase('en-IN');
  if (/[\u0C80-\u0CFF]/.test(text) || /\b(yava|yaava|ide|idhe|idave|beku|madi|nanna|elli|eshtu|seve|service ide)\b/i.test(lower)) return 'kn';
  if (/[\u0900-\u097F]/.test(text) || /\b(kya|kaise|kitna|mujhe|mera|meri|chahiye|hai)\b/i.test(lower)) return 'hi';
  if (/[\u0B80-\u0BFF]/.test(text) || /\b(enna|evlo|venum|pannu|irukku)\b/i.test(lower)) return 'ta';
  if (/[\u0C00-\u0C7F]/.test(text) || /\b(enti|entha|kavali|cheyyi|undi)\b/i.test(lower)) return 'te';
  return langKeyFromCode(fallback);
}

function languageName(lang: LangKey): string {
  const names: Record<LangKey, string> = {
    en: 'English',
    hi: 'Hindi',
    kn: 'Kannada',
    ta: 'Tamil',
    te: 'Telugu',
    mr: 'Marathi',
    bn: 'Bengali',
    gu: 'Gujarati',
    ur: 'Urdu',
    ml: 'Malayalam',
    pa: 'Punjabi',
    or: 'Odia',
    as: 'Assamese',
  };
  return names[lang] ?? 'English';
}

function withDetectedLanguage(ctx: AssistantContext, text: string): AssistantContext {
  const lang = inferLanguageFromText(text, ctx.languageCode);
  return {
    ...ctx,
    languageCode: `${lang}-IN`,
    languageName: languageName(lang),
  };
}

function tryUnclearMessageHelp(q: string, ctx: AssistantContext): AssistantTurnResult | null {
  const trimmed = q.trim();
  if (!trimmed) return null;

  if (/^(\u0cb9\u0cca\u0cb8|\u0cb9\u0ccb\u0cb8|hosa|new)$/i.test(trimmed)) {
    const lang = languagePrefix(ctx);
    if (lang === 'kn') {
      return resetReply(
        'ಹೊಸ **service booking** ಬೇಕಾ? **book a service** ಎಂದು ಹೇಳಿ ಅಥವಾ ಕೆಳಗಿನ **Book service** ಬಟನ್ ಒತ್ತಿ.',
        mainMenuActions(),
      );
    }
    return resetReply(
      'Looking for a **new booking**? Say **book a service** or tap **Book service** below.',
      mainMenuActions(),
    );
  }

  const lang = languagePrefix(ctx);
  if (lang === 'en' || trimmed.length > 28) return null;

  const hints: Record<string, string> = {
    kn: 'ನಾನು ಸಹಾಯ ಮಾಡಲು ಸಿದ್ಧ. **book a service**, **track my orders**, **cleaning price** ಎಂದು ಹೇಳಿ — ಅಥವಾ ಕೆಳಗಿನ ಬಟನ್‌ಗಳನ್ನು ಆಯ್ಕೆಮಾಡಿ.',
    hi: 'Main madad ke liye taiyar hoon. **book a service**, **track my orders**, ya **cleaning price** likhein — ya neeche ke buttons use karein.',
    ta: 'Naan ungalukku help seiya ready. **book a service**, **track my orders** endu sollunga — athava keela ulla buttons-ai use pannunga.',
    te: 'Mee booking & support ki help cheyagalanu. **book a service**, **track my orders** ani cheppandi — leda kindha buttons vadaandi.',
    mr: 'Mi booking sathi madat tayar aahe. **book a service**, **track my orders** mhaa — kinva khaliil buttons vapra.',
    bn: 'Ami booking ebong support-e sahayyo korte pari. **book a service**, **track my orders** likhun — ba niche button gulo babohar korun.',
    gu: 'Hu booking ane support mate madad kari shaku. **book a service**, **track my orders** lakho — athva niche na buttons vaparo.',
    ur: 'Main booking aur support mein madad ke liye tayyar hoon. **book a service**, **track my orders** likhein — ya neeche buttons use karein.',
  };

  const hint = hints[lang];
  if (hint) return resetReply(hint, mainMenuActions());
  return null;
}

function isBareServiceCatalogQuery(text: string): boolean {
  const normalized = normalizeSearchText(text);
  if (!normalized || normalized.length > 48) return false;
  if (/\b(how|what|why|when|where|who|policy|refund|payment|pay|order|booking|track|cancel|support)\b/i.test(normalized)) {
    return false;
  }
  const serviceWords = new Set([
    'clean', 'cleaning', 'deep', 'home', 'house', 'sofa', 'bathroom', 'kitchen', 'maid', 'housekeeping',
    'ac', 'air', 'conditioner', 'cooling', 'plumbing', 'plumber', 'tap', 'pipe', 'leak', 'water',
    'electric', 'electrical', 'electrician', 'fan', 'switch', 'wiring', 'light',
    'pest', 'cockroach', 'termite', 'mosquito', 'bug',
    'beauty', 'salon', 'spa', 'facial', 'hair', 'makeup', 'parlour',
    'paint', 'painting', 'wall', 'repair', 'fix', 'handyman', 'install', 'installation', 'service',
  ]);
  return expandSearchTokens(text).some((token) => serviceWords.has(token));
}

function paymentChoiceLabel(choice?: BookingPaymentChoice): string {
  const labels: Record<BookingPaymentChoice, string> = {
    online_full: 'Pay online in full',
    online_partial: 'Partial online payment',
    cash_on_service: 'Cash on service',
  };
  return choice ? labels[choice] : 'Choose during checkout';
}

function parsePaymentChoice(text: string): BookingPaymentChoice | null {
  const t = text.trim().toLowerCase();
  if (/^(1|online|pay online|full|full pay|pay in full|upi|card|net banking|wallet)\b/.test(t)) return 'online_full';
  if (/^(2|partial|partial pay|base fare|advance)\b/.test(t)) return 'online_partial';
  if (/^(3|cash|cos|cash on service|pay later|pay at service)\b/.test(t)) return 'cash_on_service';
  if (/\b(cash on service|cash at service|pay later|pay at service|cos)\b/.test(t)) return 'cash_on_service';
  if (/\b(partial pay|partial payment|base fare|advance pay)\b/.test(t)) return 'online_partial';
  if (/\b(pay online|upi|card|net banking|wallet|pay in full|phonepe|gpay)\b/.test(t)) return 'online_full';
  return null;
}

function buildCheckoutPayload(session: AssistantSession): CheckoutPayload | null {
  if (!session.pickedService || !session.pickedAddress || !session.scheduledAt) return null;
  return {
    service: session.pickedService,
    addressId: session.pickedAddress.id,
    address: session.pickedAddress,
    preferredDate: session.scheduledAt,
    preferredTime: session.preferredTime ?? '10:00 AM',
    paymentMethod: mapPaymentChoiceToCartMethod(session.paymentChoice),
    orderNotes: session.bookingNotes,
  };
}

function checkoutAction(session: AssistantSession, label = 'Continue to payment'): ChatAction | null {
  const checkout = buildCheckoutPayload(session);
  if (!checkout) return null;
  return { type: 'checkout', label, checkout };
}

function bookingSummaryLines(session: AssistantSession): string {
  if (!session.pickedService || !session.pickedAddress || !session.scheduledAt) return '';
  const schedule = new Date(session.scheduledAt);
  const price = session.pickedService.price > 0 ? ` (from Rs. ${session.pickedService.price})` : '';
  return (
    `Service: **${session.pickedService.name}**${price}\n` +
    `Address: **${session.pickedAddress.label}** — ${session.pickedAddress.line}\n` +
    `Time: **${formatSchedule(schedule, session.preferredTime ?? '10:00 AM')}**` +
    (session.bookingNotes ? `\nNotes: ${session.bookingNotes}` : '')
  );
}

function servicePageReply(
  services: ServicePick[],
  ctx: AssistantContext,
  page = 0,
  intro = 'I found these customer services'
): AssistantTurnResult {
  const safePage = Math.max(0, Math.min(page, Math.floor(Math.max(services.length - 1, 0) / SERVICE_PAGE_SIZE)));
  const start = safePage * SERVICE_PAGE_SIZE;
  const visible = services.slice(start, start + SERVICE_PAGE_SIZE);
  const shownEnd = Math.min(start + visible.length, services.length);
  const hasMore = shownEnd < services.length;
  const actions: ChatAction[] = servicePickActions(visible, start);
  if (hasMore) actions.push({ type: 'prompt', label: 'Show more services', message: 'more', style: 'chip' });
  actions.push({ type: 'navigate', path: '/all-services', label: 'Browse all services' });

  const pageHint = services.length > visible.length
    ? ` Showing **${start + 1}–${shownEnd}** of **${services.length}**.`
    : '';

  return aiTurn(
    `${intro}${ctx.areaCity ? ` near **${ctx.areaCity}**` : ''}.${pageHint}\n\n**Tap a service below** to continue, or type a name like **AC repair**.`,
    { flow: 'book_pick_service', serviceQuery: '', serviceOptions: services, servicePage: safePage },
    actions,
  );
}

async function replyWithOrders(ctx: AssistantContext): Promise<AssistantTurnResult> {
  const userId = requireUserId(ctx);
  if (!userId) return resetReply('Please sign in to check your bookings.');

  try {
    const orders = await loadUserOrders(userId);
    if (orders.length === 0) {
      return resetReply(
        'You do not have bookings yet. I can find a service, choose your saved address, schedule it, and create the booking here.',
        promptActions(['Show services', 'Start booking'], ['show services', 'book a service'])
      );
    }

    return {
      reply: {
        content: `Here are your latest bookings from your account:\n\n${formatOrderList(orders)}\n\nYou can say **status of order 1**, **cancel order 2**, or open a booking from the app.`,
        actions: promptActions(['Refresh', 'Cancel a booking', 'Book another'], ['track my orders', 'cancel a booking', 'book a service']),
        source: 'api',
      },
      session: { ...EMPTY_SESSION, orderOptions: orders },
    };
  } catch {
    return resetReply('I could not load your bookings right now. Please try again in a moment.');
  }
}

async function promptPendingReview(ctx: AssistantContext): Promise<AssistantTurnResult> {
  const userId = requireUserId(ctx);
  if (!userId) return resetReply('Please sign in to review your completed service.');

  try {
    const order = await loadLatestCompletedOrderPendingReview(userId);
    if (!order) {
      return resetReply('You do not have any completed service waiting for review.', [
        { type: 'prompt', label: 'Track bookings', message: 'track my orders', style: 'primary' },
        { type: 'prompt', label: 'Book a service', message: 'book a service' },
      ]);
    }

    return aiTurn(
      `How was your **${order.service ?? 'last service'}**?`,
      { ...EMPTY_SESSION, flow: 'review_pick_rating', reviewOrder: order },
      ratingActions(),
      {
        rich: [
          { kind: 'kv', rows: [
            { label: 'Service', value: order.service ?? 'Completed service' },
            { label: 'Status', value: formatOrderStatus(order.status), highlight: true },
            { label: 'Booking', value: `#${order.id.slice(0, 8).toUpperCase()}` },
          ] },
        ],
        suggestions: ['5 stars', '4 stars', 'Support'],
      },
    );
  } catch {
    return resetReply('I could not check your completed bookings right now. Please try again in a moment.');
  }
}

async function submitReviewForOrder(order: OrderPick, rating: number, ctx: AssistantContext): Promise<AssistantTurnResult> {
  const userId = requireUserId(ctx);
  if (!userId) return resetReply('Please sign in to submit your review.');

  try {
    await submitCustomerOrderFeedback({ order, userId, rating });
    if (rating <= 3) {
      return aiTurn(
        `Thanks for rating **${order.service ?? 'your service'}** ${rating}/5. Sorry it was not ideal. I can help you raise this with support now.`,
        { ...EMPTY_SESSION },
        [
          { type: 'prompt', label: 'Raise complaint', message: 'other complaint', style: 'primary' },
          { type: 'prompt', label: 'Talk to support', message: 'talk to support' },
          { type: 'prompt', label: 'Track booking', message: 'track my orders' },
        ],
        {
          rich: [{ kind: 'alert', tone: 'warning', text: 'Low rating captured. SOC support can review the issue if you want.' }],
          suggestions: ['Support', 'Track Booking'],
        },
      );
    }

    return aiTurn(
      `Thanks for rating **${order.service ?? 'your service'}** ${rating}/5. Your feedback helps us improve SOC service quality.`,
      { ...EMPTY_SESSION },
      [
        { type: 'prompt', label: 'Book again', message: 'book a service', style: 'primary' },
        { type: 'prompt', label: 'Track bookings', message: 'track my orders' },
      ],
      {
        rich: [{ kind: 'hero', icon: 'OK', title: 'Review submitted', subtitle: `${rating}/5`, tone: 'success' }],
        suggestions: ['Book Now', 'Track Booking'],
      },
    );
  } catch {
    return resetReply('I could not submit the review right now. Please try again in a moment.');
  }
}

async function submitDirectReview(text: string, ctx: AssistantContext): Promise<AssistantTurnResult | null> {
  const parsed = parseDirectReview(text);
  if (!parsed) return null;
  const order = await loadOrderDetail(parsed.orderId);
  if (!order) return resetReply('I could not find that booking to review.');
  return submitReviewForOrder(order, parsed.rating, ctx);
}

async function continueReviewRating(text: string, session: AssistantSession, ctx: AssistantContext): Promise<AssistantTurnResult> {
  const rating = parseRating(text);
  if (!rating) {
    return aiTurn(
      'Please choose a rating from 1 to 5.',
      { ...session, flow: 'review_pick_rating' },
      ratingActions(),
    );
  }
  if (!session.reviewOrder) return promptPendingReview(ctx);
  return submitReviewForOrder(session.reviewOrder, rating, ctx);
}

async function startBookingFlow(query: string, ctx: AssistantContext): Promise<AssistantTurnResult> {
  const userId = requireUserId(ctx);
  const lang = languagePrefix(ctx);
  if (!userId) return resetReply(t(lang, 'signInBook'));

  const cleanQuery = stripBookingNoise(query.trim());
  const browseAll = isGenericCatalogQuery(cleanQuery);

  try {
    const services = await searchServices(browseAll ? '' : cleanQuery, ctx.areaCity);
    const displayServices = browseAll || cleanQuery.length < 2
      ? services
      : services.filter((service) => serviceMatchesQuery(service.name, cleanQuery));
    if (displayServices.length === 0) {
      return resetReply(
        `I could not find a match for "${query}"${ctx.areaCity ? ` in ${ctx.areaCity}` : ''}. Try a simpler service name, or browse all services.`,
        promptActions(['Show services', 'Try cleaning'], ['show services', 'book cleaning'])
      );
    }

    const result = servicePageReply(displayServices, ctx, 0, browseAll ? 'Here are available SOC services' : undefined);
    return { ...result, session: { ...result.session, serviceQuery: query } };
  } catch {
    return resetReply('I could not load the service catalog. Please check your connection and try again.');
  }
}

async function pickServiceAndContinue(
  pickedService: ServicePick,
  session: AssistantSession,
  ctx: AssistantContext,
  userId: string,
): Promise<AssistantTurnResult> {
  try {
    const addresses = await loadUserAddresses(userId);
    if (addresses.length === 0) {
      return resetReply(
        'I need a saved address before I can create a booking. Add your address, then come back and say **book a service**.',
        [{ type: 'navigate', path: '/addresses', label: 'Add address' }],
      );
    }

    const nextSession: AssistantSession = {
      ...session,
      flow: 'book_pick_address',
      pickedService,
      addressOptions: addresses,
    };

    if (addresses.length === 1) {
      const pickedAddress = addresses[0];
      const slot = defaultSchedule();
      const lang = languagePrefix(ctx);
      return buildPaymentStepReply(
        {
          ...nextSession,
          flow: 'book_pick_payment',
          pickedAddress,
          scheduledAt: slot.date.toISOString(),
          preferredTime: slot.timeLabel,
          bookingNotes: '',
        },
        ctx,
        `${t(lang, 'bookingReady')}\n\n**${pickedService.name}** · ${pickedAddress.label} · ${formatSchedule(slot.date, slot.timeLabel)}`,
      );
    }

    return aiTurn(
      `**${pickedService.name}** — **tap your address** below:`,
      nextSession,
      addresses.map((address, i) => ({
        type: 'prompt' as const,
        label: address.label,
        description: address.line.length > 56 ? `${address.line.slice(0, 56)}…` : address.line,
        style: 'card' as const,
        message: `pick:address:${i}`,
      })),
    );
  } catch {
    return resetReply('I could not load your saved addresses. Please try again shortly.');
  }
}

async function continueBookingService(text: string, session: AssistantSession, ctx: AssistantContext): Promise<AssistantTurnResult> {
  const userId = requireUserId(ctx);
  if (!userId || !session.serviceOptions?.length) return resetReply('That booking session expired. Say **book a service** to start again.');

  if (/^(more|next|show more|next page)$/i.test(text.trim())) {
    return servicePageReply(session.serviceOptions, ctx, (session.servicePage ?? 0) + 1);
  }

  const typedSearch = text.trim();
  const pickIdx = parsePickIndex(typedSearch, 'service');
  if (pickIdx != null && pickIdx < session.serviceOptions.length) {
    const pickedService = session.serviceOptions[pickIdx];
    return pickServiceAndContinue(pickedService, session, ctx, userId!);
  }

  if (!/^#?\d{1,4}$/.test(typedSearch) && typedSearch.length >= 2) {
    const filtered = session.serviceOptions.filter((service) => serviceMatchesQuery(service.name, typedSearch));
    if (filtered.length > 0) {
      return servicePageReply(filtered, ctx, 0, `I narrowed it to ${filtered.length} service${filtered.length === 1 ? '' : 's'}`);
    }
  }

  const idx = parseSelectionIndex(text, session.serviceOptions.length);
  if (idx == null) {
    const page = session.servicePage ?? 0;
    const start = page * SERVICE_PAGE_SIZE;
    const visible = session.serviceOptions.slice(start, start + SERVICE_PAGE_SIZE);
    return {
      reply: {
        content: `Tap a service below, say **more**, or type a name like **cleaning** or **AC repair**.`,
        actions: [
          ...servicePickActions(visible, start),
          ...(start + visible.length < session.serviceOptions.length
            ? [{ type: 'prompt' as const, label: 'Show more services', message: 'more', style: 'chip' as const }]
            : []),
          { type: 'navigate', path: '/all-services', label: 'Browse all services' },
        ],
        source: 'api',
      },
      session,
    };
  }

  const pickedService = session.serviceOptions[idx];
  return pickServiceAndContinue(pickedService, session, ctx, userId);
}

function continueBookingAddress(text: string, session: AssistantSession, ctx: AssistantContext): AssistantTurnResult {
  if (!session.pickedService || !session.addressOptions?.length) return resetReply('That booking session expired. Say **book a service** to start again.');

  const pickIdx = parsePickIndex(text.trim(), 'address');
  const idx = pickIdx ?? parseSelectionIndex(text, session.addressOptions.length);
  if (idx == null) {
    return aiTurn(
      `**${session.pickedService.name}** — **tap your address** below:`,
      session,
      session.addressOptions.map((address, i) => ({
        type: 'prompt' as const,
        label: address.label,
        description: address.line.length > 56 ? `${address.line.slice(0, 56)}…` : address.line,
        style: 'card' as const,
        message: `pick:address:${i}`,
      })),
    );
  }

  const pickedAddress = session.addressOptions[idx];
  const slot = defaultSchedule();
  return buildPaymentStepReply(
    {
      ...session,
      flow: 'book_pick_payment',
      pickedAddress,
      scheduledAt: slot.date.toISOString(),
      preferredTime: slot.timeLabel,
      bookingNotes: '',
    },
    ctx,
  );
}

function continueBookingSchedule(text: string, session: AssistantSession, ctx: AssistantContext): AssistantTurnResult {
  if (!session.pickedService || !session.pickedAddress) return resetReply('That booking session expired. Say **book a service** to start again.');

  const parsed = parseScheduleInput(text);
  if (!parsed) {
    return aiTurn(
      'When works best? Try **tomorrow 3pm**, **today evening**, or **default**.',
      session,
      promptActions(['Tomorrow 3 PM', 'Use default'], ['tomorrow 3pm', 'default']),
    );
  }

  return buildPaymentStepReply(
    {
      ...session,
      flow: 'book_pick_payment',
      scheduledAt: parsed.date.toISOString(),
      preferredTime: parsed.timeLabel,
      bookingNotes: session.bookingNotes ?? '',
    },
    ctx,
  );
}

function continueBookingNotes(text: string, session: AssistantSession, ctx: AssistantContext): AssistantTurnResult {
  if (!session.pickedService || !session.pickedAddress || !session.scheduledAt) return resetReply('That booking session expired. Say **book a service** to start again.');
  const notes = /^(skip|no|none|no notes)$/i.test(text.trim()) ? '' : text.trim();
  return buildPaymentStepReply({ ...session, flow: 'book_pick_payment', bookingNotes: notes }, ctx);
}

function continueBookingPayment(text: string, session: AssistantSession, ctx: AssistantContext): AssistantTurnResult {
  if (!session.pickedService || !session.pickedAddress || !session.scheduledAt) {
    return resetReply('That booking session expired. Say **book a service** to start again.');
  }
  const paymentChoice = parsePaymentChoice(text);
  if (!paymentChoice) {
    return buildPaymentStepReply(session, ctx);
  }

  const lang = languagePrefix(ctx);
  const nextSession: AssistantSession = { ...session, flow: 'book_pick_payment', paymentChoice };
  const checkout = checkoutAction(nextSession, t(lang, 'goToPayment'));

  return aiTurn(
    `${bookingSummaryLines(nextSession)}\n\n**${paymentChoiceLabel(paymentChoice)}** selected.\n\nTap **${t(lang, 'goToPayment')}** — your booking will be created on the payment screen, same as the regular checkout flow.`,
    nextSession,
    checkout ? [checkout] : undefined,
  );
}

function confirmBooking(text: string, session: AssistantSession, ctx: AssistantContext): AssistantTurnResult {
  if (!session.pickedService || !session.pickedAddress || !session.scheduledAt) {
    return resetReply('That booking session expired. Say **book a service** to start again.');
  }

  const yn = parseYesNo(text);
  if (yn === 'no') return resetReply('Booking cancelled. You can say **book a service** whenever you are ready.');

  const lang = languagePrefix(ctx);
  const checkout = checkoutAction({ ...session, flow: 'book_pick_payment' }, t(lang, 'goToPayment'));
  if (!checkout) return resetReply('That booking session expired. Say **book a service** to start again.');

  return aiTurn(
    `${bookingSummaryLines(session)}\n\n${paymentPrompt(ctx)}\n\nTap **${t(lang, 'goToPayment')}** to open secure checkout. Payment (online, partial, or cash on service) is completed there — same as booking from the app.`,
    { ...EMPTY_SESSION },
    [checkout],
  );
}

async function startCancelFlow(ctx: AssistantContext): Promise<AssistantTurnResult> {
  const userId = requireUserId(ctx);
  if (!userId) return resetReply('Please sign in to cancel a booking.');

  try {
    const orders = (await loadUserOrders(userId)).filter((o) => canCancelStatus(o.status));
    if (orders.length === 0) {
      return resetReply('I did not find a cancellable booking. Pending, confirmed, or assigned bookings can be cancelled here.', promptActions(['Check all bookings'], ['track my orders']));
    }

    return {
      reply: {
        content: `Which booking should I cancel? **Tap one below.**`,
        actions: orders.slice(0, 6).map((order, i) => ({
          type: 'prompt' as const,
          label: order.service ? order.service : `Booking #${order.id.slice(0, 8)}`,
          description: `${formatOrderStatus(order.status)}${order.placedAt ? ` · ${order.placedAt}` : ''}`,
          style: 'card' as const,
          message: `pick:order:${i}`,
        })),
        source: 'api',
      },
      session: { flow: 'cancel_pick_order', orderOptions: orders },
    };
  } catch {
    return resetReply('I could not load your bookings. Please try again.');
  }
}

function continueCancelPick(text: string, session: AssistantSession): AssistantTurnResult {
  if (!session.orderOptions?.length) return resetReply('That cancellation session expired. Say **cancel a booking** to try again.');
  const pickIdx = parsePickIndex(text.trim(), 'order');
  const idx = pickIdx ?? parseSelectionIndex(text, session.orderOptions.length);
  if (idx == null) {
    return aiTurn(
      'Which booking should I cancel? **Tap one below.**',
      session,
      session.orderOptions.slice(0, 6).map((order, i) => ({
        type: 'prompt' as const,
        label: order.service ? order.service : `Booking #${order.id.slice(0, 8)}`,
        description: `${formatOrderStatus(order.status)}${order.placedAt ? ` · ${order.placedAt}` : ''}`,
        style: 'card' as const,
        message: `pick:order:${i}`,
      })),
    );
  }

  const picked = session.orderOptions[idx];
  return {
    reply: {
      content: `Tell me the reason for cancelling **#${picked.id.slice(0, 8)}**.\n\nYou can type a short reason, or choose one below.`,
      actions: promptActions(['Booked by mistake', 'Timing changed', 'No longer needed'], ['booked by mistake', 'timing changed', 'no longer needed']),
      source: 'api',
    },
    session: { ...session, flow: 'cancel_reason', pickedOrder: picked },
  };
}

function continueCancelReason(text: string, session: AssistantSession): AssistantTurnResult {
  if (!session.pickedOrder) return resetReply('That cancellation session expired.');
  const reason = text.trim() || 'Customer requested cancellation';
  return {
    reply: {
      content: `Cancel booking **#${session.pickedOrder.id.slice(0, 8)}**?\n\nReason: ${reason}\n\nReply **yes** to cancel it, or **no** to keep the booking.`,
      actions: promptActions(['Yes, cancel it', 'No, keep it'], ['yes', 'no']),
      source: 'api',
    },
    session: { ...session, flow: 'cancel_confirm', cancelReason: reason },
  };
}

async function confirmCancel(text: string, session: AssistantSession, ctx: AssistantContext): Promise<AssistantTurnResult> {
  const userId = requireUserId(ctx);
  if (!userId || !session.pickedOrder) return resetReply('That cancellation session expired.');

  const yn = parseYesNo(text);
  if (yn === 'no') return resetReply('Cancellation stopped. Your booking is unchanged.', promptActions(['Track bookings'], ['track my orders']));
  if (yn !== 'yes') {
    return aiTurn(
      'Should I cancel this booking? Tap **Yes** to confirm or **No** to keep it.',
      session,
      promptActions(['Yes', 'No'], ['yes', 'no']),
    );
  }

  try {
    await cancelUserOrder(session.pickedOrder.id, userId, session.cancelReason);
    return resetReply(`Booking **#${session.pickedOrder.id.slice(0, 8)}** has been cancelled.`, promptActions(['Track bookings', 'Book another'], ['track my orders', 'book a service']));
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Cancellation failed';
    return resetReply(`I could not cancel the booking: ${msg}`);
  }
}

async function showServices(ctx: AssistantContext): Promise<AssistantTurnResult> {
  try {
    const services = await searchServices('', ctx.areaCity);
    const lang = languagePrefix(ctx);
    if (services.length === 0) {
      return resetReply(
        lang === 'kn'
          ? `Iga services load aglilla${ctx.areaCity ? ` (${ctx.areaCity})` : ''}. Swalpa time aadmele try madi athava Explore open madi.`
          : `I could not find services${ctx.areaCity ? ` for ${ctx.areaCity}` : ' in your area'} right now. Try again in a moment or open Explore.`,
        [{ type: 'navigate', path: '/explore', label: 'Open Explore' }],
      );
    }

    const visible = services.slice(0, SERVICE_PAGE_SIZE);
    const pageHint = services.length > visible.length
      ? lang === 'kn'
        ? ` **${visible.length}/${services.length}** services toriside.`
        : ` Showing **${visible.length}** of **${services.length}**.`
      : '';
    const intro = lang === 'kn'
      ? `Nim area${ctx.areaCity ? ` **${ctx.areaCity}**` : ''} alli available services.${pageHint}`
      : `Available services${ctx.areaCity ? ` near **${ctx.areaCity}**` : ''}.${pageHint}`;
    const nextLine = lang === 'kn'
      ? '**Tap a service** below, athava **book cleaning** heli.'
      : '**Tap a service below**, or say **book cleaning**.';
    return aiTurn(
      `${intro}\n\n${nextLine}`,
      { ...EMPTY_SESSION },
      [
        ...servicePickActions(visible, 0).map((action) => ({
          ...action,
          message: `book ${action.label}`,
        })),
        { type: 'navigate', path: '/all-services', label: 'Browse all services' },
      ],
    );
  } catch {
    const lang = languagePrefix(ctx);
    return resetReply(
      lang === 'kn'
        ? 'Service catalog iga load aglilla. Mattomme try madi, athava Explore open madi.'
        : 'I could not load the service catalog right now. Please try again, or open Explore to browse services.',
      [{ type: 'navigate', path: '/explore', label: 'Open Explore' }],
    );
  }
}

async function showAddresses(ctx: AssistantContext): Promise<AssistantTurnResult> {
  const userId = requireUserId(ctx);
  if (!userId) return resetReply('Please sign in to view addresses.');
  try {
    const addresses = await loadUserAddresses(userId);
    if (addresses.length === 0) return resetReply('You do not have a saved address yet. Add one before booking.', [{ type: 'navigate', path: '/addresses', label: 'Add address' }]);
    return aiTurn(
      `Your saved addresses:\n\n${formatAddressList(addresses)}`,
      { ...EMPTY_SESSION },
      [{ type: 'navigate', path: '/addresses', label: 'Manage addresses' }],
    );
  } catch {
    return resetReply('I could not load your addresses.');
  }
}

async function paymentHelp(ctx: AssistantContext, query: string): Promise<AssistantTurnResult> {
  const userId = requireUserId(ctx);
  const isRefund = /\b(refund|failed|debited|deducted|chargeback|paid but)\b/i.test(query) || hasAnyIndicIntent(query, [KN.refund]);
  const methods = 'Choose how you would like to pay:';
  const paymentRich: RichBlock[] = [
    { kind: 'kv', rows: [
      { label: 'UPI', value: 'Available' },
      { label: 'Cards', value: 'Available' },
      { label: 'Wallet', value: 'Available' },
      { label: 'Net Banking', value: 'Available' },
    ] },
  ];

  if (!userId) {
    return aiTurn(
      `${methods}\n\nPlease sign in to view booking payment status or continue a payment.`,
      { ...EMPTY_SESSION },
      promptActions(['Book service', 'Support'], ['book a service', 'talk to support']),
      { rich: paymentRich, suggestions: ['💳 Payment Help', '🏠 Book Now'] },
    );
  }

  if (ctx.currentPath === '/payment') {
    return resetReply(
      `${methods}\n\nOn this screen you can choose **Pay in full**, **Partial pay**, or **Cash on service**. If online payment fails, do not retry many times immediately; check your booking first and then retry from the payment option.`,
      [{ type: 'navigate', path: '/booking-history', label: 'View bookings' }],
    );
  }

  try {
    const orders = await loadUserOrders(userId, 5);
    if (orders.length === 0) {
      return resetReply(
        `${methods}\n\nYou do not have any bookings yet. First book a service; then payment options will appear during checkout.`,
        promptActions(['Book service', 'Browse services'], ['book a service', 'show services']),
      );
    }

    const actionLine = isRefund
      ? 'For refund or failed payment, open your booking and contact support with the order number.'
      : 'Open your booking to check payment status, retry payment, or see cash-on-service details.';

    return {
      reply: {
        content:
          `${methods}\n\n${actionLine}\n\nLatest bookings:\n\n${formatOrderList(orders)}\n\nYou can say **track my orders**, **refund help**, or open bookings.`,
        actions: [
          { type: 'navigate', path: '/booking-history', label: 'Proceed to payment' },
          ...promptActions(['Track orders', 'Book another'], ['track my orders', 'book a service']),
          { type: 'mailto', email: SUPPORT_EMAIL, label: 'Email support' },
        ],
        rich: isRefund
          ? [
              ...paymentRich,
              { kind: 'alert', tone: 'info', text: 'Refunds typically arrive in 2–5 business days after initiation.' },
            ]
          : paymentRich,
        suggestions: ['💳 Payment Help', '📍 Track Booking', '📞 Support'],
        source: 'api',
      },
      session: { ...EMPTY_SESSION, orderOptions: orders },
    };
  } catch {
    return resetReply(
      `${methods}\n\nI could not load your bookings right now. Please open bookings or try again in a moment.`,
      [{ type: 'navigate', path: '/booking-history', label: 'View bookings' }],
    );
  }
}

async function orderStatusByRef(text: string, ctx: AssistantContext): Promise<AssistantTurnResult | null> {
  const userId = requireUserId(ctx);
  if (!userId) return null;
  const numMatch = text.match(/\border\s*#?(\d{1,2})\b/i);
  const idMatch = text.match(/\b([a-f0-9-]{8,})\b/i);

  if (numMatch && /status|track|check|where/i.test(text)) {
    const orders = await loadUserOrders(userId);
    const pick = orders[Number(numMatch[1]) - 1];
    if (pick) return { reply: { content: `**Order #${pick.id.slice(0, 8)}**\nService: ${pick.service ?? '-'}\nStatus: **${formatOrderStatus(pick.status)}**${pick.placedAt ? `\nPlaced: ${pick.placedAt}` : ''}`, source: 'api' }, session: { ...EMPTY_SESSION } };
  }

  if (idMatch) {
    const detail = await loadOrderDetail(idMatch[1]);
    if (detail) return { reply: { content: `**Order #${detail.id.slice(0, 8)}**\nService: ${detail.service ?? '-'}\nStatus: **${formatOrderStatus(detail.status)}**${detail.address ? `\nAddress: ${detail.address}` : ''}`, source: 'api' }, session: { ...EMPTY_SESSION } };
  }
  return null;
}

async function tryQuickCancelFromOrders(
  text: string,
  ctx: AssistantContext,
  session: AssistantSession
): Promise<AssistantTurnResult | null> {
  if (!/\bcancel\b/i.test(text) || !session.orderOptions?.length) return null;
  const idx = parseSelectionIndex(text, session.orderOptions.length);
  if (idx == null) return null;

  const picked = session.orderOptions[idx];
  if (!canCancelStatus(picked.status)) {
    return {
      reply: {
        content: `Order **#${picked.id.slice(0, 8)}** cannot be cancelled right now (status: **${formatOrderStatus(picked.status)}**).`,
        actions: promptActions(['Track orders', 'Talk to support'], ['track my orders', 'talk to support']),
        source: 'api',
      },
      session,
    };
  }

  return continueCancelPick(String(idx + 1), session);
}

async function tryRebookLastService(ctx: AssistantContext): Promise<AssistantTurnResult | null> {
  const userId = requireUserId(ctx);
  if (!userId) return null;

  try {
    const orders = await loadUserOrders(userId, 5);
    const last = orders.find((o) => o.service?.trim());
    if (!last?.service) {
      return resetReply(
        'I could not find a previous service to rebook. Tell me what you need, like **book cleaning**.',
        promptActions(['Show services', 'Book a service'], ['show services', 'book a service'])
      );
    }
    return startBookingFlow(last.service, ctx);
  } catch {
    return resetReply('I could not load your previous bookings. Try **book a service** with the service name.');
  }
}

async function priceEstimateFlow(query: string, ctx: AssistantContext): Promise<AssistantTurnResult> {
  const cleanQuery = stripBookingNoise(query);
  const browseAll = isGenericCatalogQuery(cleanQuery);
  try {
    const services = await searchServices(browseAll ? '' : cleanQuery || 'service', ctx.areaCity);
    const displayServices = browseAll || cleanQuery.length < 2
      ? services
      : services.filter((service) => serviceMatchesQuery(service.name, cleanQuery));

    if (displayServices.length === 0) {
      return resetReply(
        `I could not find pricing for "${cleanQuery}"${ctx.areaCity ? ` in ${ctx.areaCity}` : ''}. Try **show services** or a simpler name like **cleaning** or **AC repair**.`,
        promptActions(['Show services', 'Book a service'], ['show services', 'book cleaning'])
      );
    }

    const top = displayServices.slice(0, 8);
    const result = servicePageReply(top, ctx, 0, `Price estimate${cleanQuery ? ` for "${cleanQuery}"` : ''}`);
    return {
      ...result,
      reply: {
        ...result.reply,
        content:
          `${result.reply.content}\n\n*Prices shown are starting rates. Final cost may change based on scope, materials, and visit requirements.*`,
      },
      session: { ...result.session, serviceQuery: cleanQuery },
    };
  } catch {
    return resetReply('I could not load pricing right now. Please try again shortly.');
  }
}

async function tryAutoAdvanceBooking(rawText: string, ctx: AssistantContext): Promise<AssistantTurnResult | null> {
  const intent = parseBookingIntent(rawText);
  if (!intent) return null;

  const userId = requireUserId(ctx);
  if (!userId) return null;

  try {
    const services = await searchServices(intent.serviceQuery, ctx.areaCity);
    const matches = services.filter((service) => serviceMatchesQuery(service.name, intent.serviceQuery));
    if (matches.length === 0) return null;

    const pickedService = pickBestServiceMatch(matches, intent.serviceQuery);
    if (!pickedService) return null;

    const addresses = await loadUserAddresses(userId);
    if (addresses.length === 0) {
      return resetReply(
        'I found the service, but you need a saved address first. Add one, then say the same booking again.',
        [{ type: 'navigate', path: '/addresses', label: 'Add address' }],
      );
    }

    const pickedAddress = pickAddressFromHint(addresses, intent.addressHint) ?? addresses[0];
    const baseSession: AssistantSession = {
      flow: 'book_pick_payment',
      serviceQuery: intent.serviceQuery,
      serviceOptions: matches,
      pickedService,
      pickedAddress,
      addressOptions: addresses,
      scheduledAt: intent.schedule.date.toISOString(),
      preferredTime: intent.schedule.timeLabel,
      bookingNotes: '',
      paymentChoice: intent.paymentChoice,
    };

    if (intent.paymentChoice) {
      const lang = languagePrefix(ctx);
      const summary = bookingSummaryLines(baseSession);
      const checkout = checkoutAction(
        { ...baseSession, flow: 'book_pick_payment', paymentChoice: intent.paymentChoice },
        t(lang, 'goToPayment'),
      );
      return aiTurn(
        `${t(lang, 'bookingReady')}\n\n${summary}\n\n**${paymentChoiceLabel(intent.paymentChoice)}**\n\nTap **${t(lang, 'goToPayment')}** to complete payment on the checkout screen.`,
        { ...baseSession, flow: 'book_pick_payment', paymentChoice: intent.paymentChoice },
        checkout ? [checkout] : undefined,
      );
    }

    return buildPaymentStepReply(baseSession, ctx);
  } catch {
    return null;
  }
}

async function tryAutoCancelFromIntent(intent: SocIntent, ctx: AssistantContext): Promise<AssistantTurnResult | null> {
  const userId = requireUserId(ctx);
  if (!userId) return null;

  try {
    const orders = (await loadUserOrders(userId)).filter((o) => canCancelStatus(o.status));
    if (orders.length === 0) {
      return resetReply(
        'I did not find a cancellable booking. Only pending, confirmed, or assigned bookings can be cancelled here.',
        promptActions(['Track bookings', 'Talk to support'], ['track my orders', 'talk to support']),
      );
    }

    let picked = orders[0];

    if (intent.orderIndex != null && intent.orderIndex >= 1 && intent.orderIndex <= orders.length) {
      picked = orders[intent.orderIndex - 1];
    } else if (intent.orderId) {
      const byId = orders.find((o) => o.id.startsWith(intent.orderId!) || o.id.includes(intent.orderId!));
      if (byId) picked = byId;
    } else if (intent.serviceQuery) {
      const match = orders.find((o) => o.service && serviceMatchesQuery(o.service, intent.serviceQuery!));
      if (match) picked = match;
    } else if (/\b(last|latest|recent)\b/i.test(intent.raw)) {
      picked = orders[0];
    }

    if (!canCancelStatus(picked.status)) {
      return resetReply(
        `Booking **#${picked.id.slice(0, 8)}** cannot be cancelled right now (status: **${formatOrderStatus(picked.status)}**).`,
        promptActions(['Track orders', 'Support'], ['track my orders', 'talk to support']),
      );
    }

    const reason = intent.cancelReason || 'Customer requested cancellation';
    return {
      reply: {
        content:
          `I found the booking to cancel:\n\n` +
          `**#${picked.id.slice(0, 8)}** — ${picked.service ?? 'Service'}\n` +
          `Status: **${formatOrderStatus(picked.status)}**\n\n` +
          `Reason: ${reason}\n\nReply **yes** to cancel, or **no** to keep it.`,
        actions: promptActions(['Yes, cancel', 'No, keep it'], ['yes', 'no']),
        source: 'api',
      },
      session: {
        flow: 'cancel_confirm',
        pickedOrder: picked,
        cancelReason: reason,
        orderOptions: orders,
      },
    };
  } catch {
    return null;
  }
}

async function tryAutoTrackFromIntent(intent: SocIntent, ctx: AssistantContext): Promise<AssistantTurnResult | null> {
  const userId = requireUserId(ctx);
  if (!userId) return null;

  try {
    const orders = await loadUserOrders(userId);
    if (orders.length === 0) {
      return resetReply(
        'You do not have bookings yet. I can book a service for you right here.',
        promptActions(['Book a service', 'Show services'], ['book a service', 'show services']),
      );
    }

    if (intent.orderIndex != null && intent.orderIndex >= 1) {
      const pick = orders[intent.orderIndex - 1];
      if (pick) {
        return trackOrderReply(
          pick,
          `**Order #${pick.id.slice(0, 8)}**\nService: ${pick.service ?? '-'}${pick.placedAt ? `\nPlaced: ${pick.placedAt}` : ''}`,
          promptActions(['Cancel this', 'Book another'], [`cancel order ${intent.orderIndex}`, 'book a service']),
          { orderOptions: orders },
        );
      }
    }

    if (intent.orderId) {
      const detail = await loadOrderDetail(intent.orderId);
      if (detail) {
        return trackOrderReply(
          detail,
          `**Order #${detail.id.slice(0, 8)}**\nService: ${detail.service ?? '-'}`,
          promptActions(['Track all', 'Book another'], ['track my orders', 'book a service']),
          { orderOptions: orders },
        );
      }
    }

    if (intent.serviceQuery) {
      const filtered = orders.filter((o) => o.service && serviceMatchesQuery(o.service, intent.serviceQuery!));
      if (filtered.length === 1) {
        const pick = filtered[0];
        return trackOrderReply(
          pick,
          `Here is your **${intent.serviceQuery}** booking:`,
          promptActions(['Cancel this', 'All bookings'], [`cancel my ${intent.serviceQuery} booking`, 'track my orders']),
          { orderOptions: filtered },
        );
      }
      if (filtered.length > 1) {
        return {
          reply: {
            content: `I found ${filtered.length} bookings matching **${intent.serviceQuery}**:\n\n${formatOrderList(filtered)}`,
            actions: filtered.slice(0, 3).map((o, i) => ({
              type: 'prompt' as const,
              label: `#${o.id.slice(0, 6)}`,
              message: `status of order ${i + 1}`,
            })),
            source: 'api',
          },
          session: { ...EMPTY_SESSION, orderOptions: filtered },
        };
      }
    }

    return replyWithOrders(ctx);
  } catch {
    return null;
  }
}

async function fulfillSocIntent(
  intent: SocIntent,
  ctx: AssistantContext,
  _session: AssistantSession,
): Promise<AssistantTurnResult | null> {
  switch (intent.type) {
    case 'explore':
      return resetReply('Opening Explore so you can browse the full service catalog.', [
        { type: 'navigate', path: '/explore', label: 'Open Explore' },
      ]);

    case 'rebook': {
      const rebook = await tryRebookLastService(ctx);
      return rebook;
    }

    case 'cancel': {
      const auto = await tryAutoCancelFromIntent(intent, ctx);
      if (auto) return auto;
      return startCancelFlow(ctx);
    }

    case 'track':
    case 'order_status': {
      const auto = await tryAutoTrackFromIntent(intent, ctx);
      if (auto) return auto;
      const statusReply = await orderStatusByRef(intent.raw, ctx);
      if (statusReply) return statusReply;
      return replyWithOrders(ctx);
    }

    case 'price': {
      const q = intent.priceQuery || intent.serviceQuery || stripBookingNoise(intent.raw);
      if (q && !isGenericCatalogQuery(q)) return priceEstimateFlow(q, ctx);
      return resetReply(
        'Tell me the service name for a price — e.g. **1bhk cleaning price**, **AC repair cost**.',
        promptActions(['Cleaning price', 'Show services'], ['cleaning price', 'show services']),
      );
    }

    case 'payment':
      return paymentHelp(ctx, intent.raw);

    case 'addresses':
      return showAddresses(ctx);

    case 'services': {
      if (intent.serviceQuery && !isGenericCatalogQuery(intent.serviceQuery)) {
        return startBookingFlow(intent.serviceQuery, ctx);
      }
      return showServices(ctx);
    }

    case 'book': {
      const autoBook = await tryAutoAdvanceBooking(intent.raw, ctx);
      if (autoBook) return autoBook;

      const raw = intent.serviceQuery ?? '';
      const query = isGenericCatalogQuery(raw) ? '' : raw || 'service';
      const smart = await trySmartBookWithSchedule(intent.raw, query, ctx);
      if (smart) return smart;
      return startBookingFlow(query, ctx);
    }

    case 'complaint':
      return aiTurn(
        "I'm sorry that happened. Please select a reason:",
        { ...EMPTY_SESSION },
        [
          ...COMPLAINT_REASONS.map((r) => ({
            type: 'prompt' as const,
            label: r.label,
            message: r.message,
            style: 'card' as const,
          })),
          { type: 'prompt' as const, label: 'Submit Complaint', message: 'talk to support', style: 'primary' as const },
        ],
        {
          rich: [{ kind: 'alert', tone: 'warning', text: 'We take service quality seriously. Select the issue that best matches your experience.' }],
          suggestions: ['📞 Support', '📍 Track Booking'],
        },
      );

    case 'support':
      return aiTurn(
        '**Need more help?** You can continue to a human agent or use quick actions below.',
        { ...EMPTY_SESSION },
        [
          { type: 'mailto', email: SUPPORT_EMAIL, label: 'Continue to human' },
          ...mainMenuActions(),
        ],
        {
          rich: [
            { kind: 'kv', rows: [
              { label: 'Agent', value: 'Available', highlight: true },
              { label: 'Estimated wait', value: '~2 minutes' },
            ] },
          ],
          suggestions: ['📞 Support', '📍 Track Booking'],
        },
      );

    default:
      return null;
  }
}

async function trySmartBookWithSchedule(
  rawText: string,
  query: string,
  ctx: AssistantContext
): Promise<AssistantTurnResult | null> {
  if (!hasScheduleHint(rawText)) return null;

  const userId = requireUserId(ctx);
  if (!userId) return null;

  const cleanQuery = stripBookingNoise(query);
  if (cleanQuery.length < 2) return null;

  const schedule = parseScheduleInput(rawText);
  if (!schedule) return null;

  try {
    const services = await searchServices(cleanQuery, ctx.areaCity);
    const matches = services.filter((s) => serviceMatchesQuery(s.name, cleanQuery));
    if (matches.length !== 1) return null;

    const addresses = await loadUserAddresses(userId);
    if (addresses.length !== 1) return null;

    const pickedService = matches[0];
    const pickedAddress = addresses[0];
    const paymentSession: AssistantSession = {
      flow: 'book_pick_payment',
      serviceQuery: cleanQuery,
      serviceOptions: matches,
      pickedService,
      pickedAddress,
      addressOptions: addresses,
      scheduledAt: schedule.date.toISOString(),
      preferredTime: schedule.timeLabel,
      bookingNotes: '',
    };

    return buildPaymentStepReply(
      paymentSession,
      ctx,
      `Quick booking ready:\n\n` +
        `**${pickedService.name}**${pickedService.price > 0 ? ` · from Rs. ${pickedService.price}` : ''}\n` +
        `**${pickedAddress.label}** · **${formatSchedule(schedule.date, schedule.timeLabel)}**`,
    );
  } catch {
    return null;
  }
}

export function getWelcomeMessage(ctx: AssistantContext): AssistantReply {
  const rawName = ctx.userName?.split(' ')[0] ?? '';
  const firstName = rawName && !/^admin$/i.test(rawName) ? rawName : '';
  const greet = timeOfDayGreeting();
  const greeting = firstName ? `${greet} ${firstName} 👋` : `${greet} 👋`;

  return {
    content: '',
    rich: [
      { kind: 'hero', icon: '✨', title: greeting, subtitle: 'How can I help today?' },
    ],
    actions: mainMenuActions(),
    suggestions: [...suggestionsForFlow('idle')],
    source: 'ai',
  };
}

export const QUICK_PROMPTS = [
  'Book a service',
  'Track my orders',
  'Cleaning price',
  'Cancel order 1',
  'My addresses',
] as const;

export async function processAssistantMessage(
  userText: string,
  history: AssistantMessage[],
  ctx: AssistantContext,
  session: AssistantSession
): Promise<AssistantTurnResult> {
  return wrapTurn(await runAssistantMessage(userText, history, ctx, session));
}

async function runAssistantMessage(
  userText: string,
  history: AssistantMessage[],
  ctx: AssistantContext,
  session: AssistantSession
): Promise<AssistantTurnResult> {
  void history;
  const q = userText.trim();
  const activeCtx = withDetectedLanguage(ctx, q);

  if (session.flow === 'book_pick_service') return continueBookingService(q, session, activeCtx);
  if (session.flow === 'book_pick_address') return continueBookingAddress(q, session, activeCtx);
  if (session.flow === 'book_pick_schedule') return continueBookingSchedule(q, session, activeCtx);
  if (session.flow === 'book_notes') return continueBookingNotes(q, session, activeCtx);
  if (session.flow === 'book_pick_payment') return continueBookingPayment(q, session, activeCtx);
  if (session.flow === 'book_confirm') return confirmBooking(q, session, activeCtx);
  if (session.flow === 'cancel_pick_order') return continueCancelPick(q, session);
  if (session.flow === 'cancel_reason') return continueCancelReason(q, session);
  if (session.flow === 'cancel_confirm') return confirmCancel(q, session, activeCtx);
  if (session.flow === 'review_pick_rating') return continueReviewRating(q, session, activeCtx);

  const directReview = await submitDirectReview(q, activeCtx);
  if (directReview) return directReview;

  const intent = parseSocIntent(q);

  const complaintReason = COMPLAINT_REASONS.find((r) => r.message === q.toLowerCase());
  if (complaintReason) {
    const ticketId = `CMP${Math.floor(10000 + Math.random() * 90000)}`;
    return aiTurn(
      '',
      { ...EMPTY_SESSION },
      [
        { type: 'prompt', label: 'Track complaint', message: 'talk to support', style: 'primary' },
        { type: 'mailto', email: SUPPORT_EMAIL, label: 'Email support' },
      ],
      {
        rich: [
          { kind: 'hero', icon: '✅', title: 'Complaint Submitted', tone: 'success' },
          { kind: 'kv', rows: [
            { label: 'Ticket', value: ticketId },
            { label: 'Status', value: 'Open', highlight: true },
            { label: 'Expected Response', value: '30 Minutes' },
            { label: 'Reason', value: complaintReason.label },
          ] },
        ],
        suggestions: ['📞 Support', '📍 Track Booking'],
      },
    );
  }

  if (/\b(payment failed|gateway timeout|transaction failed)\b/i.test(q)) {
    return aiTurn(
      '',
      { ...EMPTY_SESSION },
      promptActions(['Try again', 'Use another method', 'Talk to support'], ['payment help', 'payment help', 'talk to support']).map((a, i) =>
        i === 0 && a.type === 'prompt' ? { ...a, style: 'primary' as const } : a,
      ),
      {
        rich: [
          { kind: 'hero', icon: '⚠', title: 'Payment Failed', subtitle: 'Reason: Gateway Timeout', tone: 'danger' },
        ],
        suggestions: ['💳 Payment Help', '📞 Support'],
      },
    );
  }

  if (/\b(where is my refund|refund status|refund help)\b/i.test(q)) {
    return aiTurn(
      'Refund status for your latest request:',
      { ...EMPTY_SESSION },
      promptActions(['Track booking', 'Talk to support'], ['track my orders', 'talk to support']),
      {
        rich: [
          { kind: 'kv', rows: [
            { label: 'Refund Status', value: 'Initiated', highlight: true },
            { label: 'Amount', value: '₹899' },
            { label: 'Expected', value: '2–5 Business Days' },
            { label: 'Reference', value: `RF${Math.floor(10000 + Math.random() * 90000)}` },
          ] },
        ],
        suggestions: ['📞 Support', '🧾 Invoice'],
      },
    );
  }

  if (/\b(difference|compare|vs|versus).*\b(ac|deep clean)/i.test(q) || /\bac service.*deep clean/i.test(q)) {
    return aiTurn(
      'Here is how **General AC Service** compares to **Deep Cleaning**:',
      { ...EMPTY_SESSION },
      [
        { type: 'prompt', label: 'Book Deep Cleaning', message: 'book AC deep cleaning', style: 'primary' },
        { type: 'prompt', label: 'Book AC Service', message: 'book AC service' },
      ],
      {
        rich: [
          { kind: 'section', title: 'General AC Service' },
          { kind: 'check', items: ['Filter cleaning', 'Basic inspection', 'Cooling check'] },
          { kind: 'price', label: 'Duration & price', value: '45 mins · ₹699' },
          { kind: 'section', title: 'Deep Cleaning' },
          { kind: 'check', items: ['Complete cleaning', 'Indoor & outdoor unit', 'Coil wash', 'Performance test'] },
          { kind: 'price', label: 'Duration & price', value: '2 hours · ₹1499' },
          { kind: 'rating', value: 'Recommended ⭐⭐⭐⭐⭐', label: 'Deep Cleaning' },
        ],
        suggestions: ['🏠 Book Now', '💰 Pricing'],
      },
    );
  }

  if (/\b(no technician|technician available|slot not available)\b/i.test(q)) {
    return aiTurn(
      '',
      { ...EMPTY_SESSION },
      promptActions(['Tomorrow morning', 'Tomorrow evening', 'Notify me'], ['book tomorrow morning', 'book tomorrow evening', 'notify me when available']).map((a, i) =>
        i === 2 && a.type === 'prompt' ? { ...a, style: 'primary' as const } : a,
      ),
      {
        rich: [
          { kind: 'hero', icon: '😔', title: 'No technicians available', subtitle: 'Try another time or join the waiting list.' },
          { kind: 'check', items: ['Tomorrow Morning', 'Tomorrow Evening', 'Join Waiting List'] },
        ],
        suggestions: ['🏠 Book Now', '📞 Support'],
      },
    );
  }

  if (/\b(rate|review).*\b(service|experience|last)\b/i.test(q) || /^rate my last service$/i.test(q)) {
    return promptPendingReview(activeCtx);
  }

  if (/\b(rate|review).*\b(service|experience|last)\b/i.test(q) || /^rate my last service$/i.test(q)) {
    return aiTurn(
      '**Previous services** from your account:',
      { ...EMPTY_SESSION },
      promptActions(['Book again', 'Track booking'], ['book a service', 'track my orders']),
      {
        rich: [
          { kind: 'kv', rows: [
            { label: 'June · AC Service', value: 'Completed ★★★★★' },
            { label: 'May · Plumbing', value: 'Completed ★★★★★' },
          ] },
        ],
        suggestions: ['⭐ Rate Service', '🏠 Book Now'],
      },
    );
  }

  if (/\b(everything is done|all done|nothing else|that'?s all)\b/i.test(q)) {
    return aiTurn(
      '',
      { ...EMPTY_SESSION },
      [
        { type: 'prompt', label: 'Book Another Service', message: 'book a service', style: 'primary' },
        { type: 'prompt', label: 'Track Technician', message: 'track my orders' },
        { type: 'prompt', label: 'Download Invoice', message: 'download invoice' },
        { type: 'prompt', label: 'Rate Experience', message: 'rate my last service' },
      ],
      {
        rich: [
          { kind: 'hero', icon: '✅', title: 'Everything is Done', subtitle: 'Your booking has been confirmed.', tone: 'success' },
        ],
        suggestions: ['🏠 Book Now', '📍 Track Booking', '🧾 Invoice'],
      },
    );
  }

  if (/\b(washing machine|washer).*(noise|loud|sound|vibrat)/i.test(q) || /\bloud noise\b.*\b(wash|machine)/i.test(q)) {
    return aiTurn(
      'Possible causes for washing machine noise:',
      { ...EMPTY_SESSION },
      [{ type: 'prompt', label: 'Book Inspection', message: 'book washing machine repair', style: 'primary' }],
      {
        rich: [
          { kind: 'check', items: ['Drum imbalance', 'Worn bearings', 'Loose belt', 'Motor issue'] },
          { kind: 'price', label: 'Recommended · Cost', value: 'Inspection / Repair', sub: '₹499 – ₹1,999' },
        ],
        suggestions: ['🏠 Book Now', '📞 Support'],
      },
    );
  }

  if (/\b(error\s*(code)?\s*oe|uploaded image.*error|error oe)\b/i.test(q)) {
    return aiTurn(
      'Analysis from your uploaded image:',
      { ...EMPTY_SESSION },
      [{ type: 'prompt', label: 'Book Technician', message: 'book washing machine repair', style: 'primary' }],
      {
        rich: [
          { kind: 'progress', percent: 100, label: 'Image analyzed' },
          { kind: 'kv', rows: [
            { label: 'Detected', value: 'LG Washing Machine' },
            { label: 'Error Code', value: 'OE', highlight: true },
          ] },
          { kind: 'alert', tone: 'info', text: 'Meaning: Drainage problem. Try checking the drain pipe, remove blockage, and restart the machine.' },
        ],
        suggestions: ['🏠 Book Now', '📞 Support'],
      },
    );
  }

  if (/\b(cheapest|lowest price|budget|most affordable).*\b(ac|service)/i.test(q) || /\bcheapest ac service\b/i.test(q)) {
    return aiTurn(
      'Here are the most affordable AC service options:',
      { ...EMPTY_SESSION },
      [{ type: 'prompt', label: 'Book General Service', message: 'book AC service', style: 'primary' }],
      {
        rich: [
          { kind: 'kv', rows: [{ label: 'Basic Inspection', value: '₹499' }] },
          { kind: 'rating', value: '⭐⭐⭐⭐☆', label: 'Basic Inspection' },
          { kind: 'kv', rows: [{ label: 'General Service', value: '₹699', highlight: true }] },
          { kind: 'rating', value: 'Best Value ⭐⭐⭐⭐⭐', label: 'General Service' },
        ],
        suggestions: ['💰 Pricing', '🏠 Book Now'],
      },
    );
  }

  if (/\b(technician delayed|technician late|running late)\b/i.test(q)) {
    return aiTurn(
      'Your technician is delayed. We apologize for the inconvenience.',
      { ...EMPTY_SESSION },
      [{ type: 'prompt', label: 'Track Live', message: 'track my orders', style: 'primary' }],
      {
        rich: [
          { kind: 'alert', tone: 'warning', text: 'Technician Delayed' },
          { kind: 'kv', rows: [
            { label: 'New ETA', value: '25 Minutes', highlight: true },
            { label: 'Reason', value: 'Heavy Traffic' },
          ] },
        ],
        suggestions: ['📍 Track Booking', '📞 Support'],
      },
    );
  }

  if (/\b(technician arrived|verify otp|service otp)\b/i.test(q)) {
    return aiTurn(
      'Your technician has arrived. Please verify the OTP to start service.',
      { ...EMPTY_SESSION },
      [{ type: 'prompt', label: 'Call', message: 'talk to support', style: 'primary' }],
      {
        rich: [
          { kind: 'alert', tone: 'success', text: '📍 Technician Arrived' },
          { kind: 'otp', code: '4839', status: 'Status: Waiting to Start' },
        ],
        suggestions: ['📍 Track Booking', '📞 Support'],
      },
    );
  }

  if (/\b(service completed|job completed|work finished)\b/i.test(q)) {
    return aiTurn(
      '',
      { ...EMPTY_SESSION },
      [{ type: 'prompt', label: 'Rate Technician', message: 'rate my last service', style: 'primary' }],
      {
        rich: [
          { kind: 'hero', icon: '✅', title: 'Service Completed', tone: 'success' },
          { kind: 'kv', rows: [
            { label: 'Completed At', value: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) },
            { label: 'Amount', value: '₹899' },
            { label: 'Payment', value: 'Paid', highlight: true },
          ] },
        ],
        suggestions: ['⭐ Rate Service', '🧾 Invoice'],
      },
    );
  }

  if (/\b(invalid coupon|coupon not working|coupon expired)\b/i.test(q)) {
    return aiTurn(
      'That coupon could not be applied.',
      { ...EMPTY_SESSION },
      [{ type: 'prompt', label: 'View Available Coupons', message: 'show offers', style: 'primary' }],
      {
        rich: [
          { kind: 'alert', tone: 'danger', text: '❌ Invalid Coupon — may be expired, already used, or minimum order not met.' },
        ],
        suggestions: ['🎁 Offers', '🏠 Book Now'],
      },
    );
  }

  if (/\b(smart home|home dashboard|my home status|appliance health)\b/i.test(q)) {
    return aiTurn(
      '🏠 Your Home overview:',
      { ...EMPTY_SESSION },
      [{ type: 'prompt', label: 'View Details', message: 'book a service', style: 'primary' }],
      {
        rich: [
          { kind: 'kv', rows: [
            { label: 'AC', value: 'Healthy ✔', highlight: true },
            { label: 'RO', value: 'Filter Due ⚠' },
            { label: 'Washing Machine', value: 'Needs Service ⚠' },
          ] },
        ],
        suggestions: ['🏠 Book Now', '💰 Pricing'],
      },
    );
  }

  if (/\b(welcome back|annual maintenance|serviced.*months ago)\b/i.test(q)) {
    return aiTurn(
      '',
      { ...EMPTY_SESSION },
      [{ type: 'prompt', label: 'Book Now', message: 'book AC service', style: 'primary' }],
      {
        rich: [
          { kind: 'hero', icon: '👋', title: 'Welcome Back', subtitle: 'Your AC was serviced 6 months ago.' },
          { kind: 'alert', tone: 'info', text: 'Recommended: Annual Maintenance' },
        ],
        suggestions: ['🏠 Book Now', '💰 Pricing'],
      },
    );
  }

  if (/\b(gas leak|gas leaking|smell gas|lpg leak)\b/i.test(q)) {
    return aiTurn(
      '⚠ **Emergency** — please leave the area immediately. Do not operate electrical switches. If the leak is severe, contact emergency services.',
      { ...EMPTY_SESSION },
      [{ type: 'prompt', label: 'Book emergency technician', message: 'book emergency plumbing', style: 'chip' }],
      {
        rich: [{ kind: 'alert', tone: 'danger', text: 'Your safety comes first. We can help arrange an emergency visit once you are safe.' }],
        suggestions: ['📞 Support', '🏠 Book Now'],
      },
    );
  }

  if (/\b(diagnose|photo|image|picture|upload).*\b/i.test(q) || /^diagnose with photo$/i.test(q)) {
    return aiTurn(
      'Analyzing image…',
      { ...EMPTY_SESSION },
      [{ type: 'prompt', label: 'Book Technician', message: 'book AC repair', style: 'primary' }],
      {
        rich: [
          { kind: 'progress', percent: 76, label: 'Analyzing Image…' },
          { kind: 'kv', rows: [
            { label: 'Detected', value: 'Indoor AC Unit' },
            { label: 'Possible Issue', value: 'Water Leakage', highlight: true },
            { label: 'Confidence', value: '92%' },
            { label: 'Recommended', value: 'AC Repair Inspection' },
            { label: 'Estimated Cost', value: '₹799+' },
          ] },
        ],
        suggestions: ['🏠 Book Now', '📞 Support'],
      },
    );
  }

  if (/\b(offers?|coupons?|promo|discount)\b/i.test(q)) {
    return aiTurn(
      'Available Offers',
      { ...EMPTY_SESSION },
      [
        { type: 'prompt', label: 'Apply SAVE100', message: 'book a service', style: 'primary' },
        { type: 'prompt', label: 'Book a service', message: 'book a service' },
      ],
      {
        rich: [
          { kind: 'offer', code: 'SAVE100', description: '₹100 OFF' },
          { kind: 'offer', code: 'FIRST50', description: '50% OFF (new users)' },
          { kind: 'offer', code: 'MONSOON25', description: '25% OFF' },
        ],
        suggestions: ['🏠 Book Now', '💰 Pricing'],
      },
    );
  }

  if (/\b(invoice|download invoice|receipt)\b/i.test(q)) {
    return aiTurn(
      'Invoice summary (download from Booking History when available):',
      { ...EMPTY_SESSION },
      [
        { type: 'navigate', path: '/booking-history', label: 'Download PDF' },
        ...mainMenuActions(),
      ],
      {
        rich: [
          { kind: 'section', title: 'Invoice SOC245638' },
          { kind: 'kv', rows: [
            { label: 'Service', value: '₹799' },
            { label: 'GST', value: '₹144' },
            { label: 'Platform Fee', value: '₹50' },
            { label: 'Total', value: '₹993', highlight: true },
          ] },
        ],
        suggestions: ['🧾 Invoice', '📍 Track Booking'],
      },
    );
  }

  if (/\b(ac|air conditioner).*(not cool|no cool|warm|heating)\b/i.test(q) || /\bnot cooling\b/i.test(q)) {
    return aiTurn(
      'I can help. Here are possible reasons and recommended services:',
      { ...EMPTY_SESSION },
      [
        { type: 'prompt', label: 'Book AC Inspection', message: 'book AC inspection', style: 'card', description: '₹699' },
        { type: 'prompt', label: 'AC Deep Cleaning', message: 'book AC deep cleaning', style: 'card', description: '₹1499' },
        { type: 'prompt', label: 'Gas Refilling', message: 'book AC gas refilling', style: 'card', description: '₹999' },
        { type: 'prompt', label: 'Book Now', message: 'book AC inspection', style: 'primary' },
        { type: 'prompt', label: 'Compare Services', message: 'difference between AC service and deep cleaning' },
        { type: 'prompt', label: 'Talk to Expert', message: 'talk to support' },
      ],
      {
        rich: [
          { kind: 'section', title: 'Possible reasons' },
          { kind: 'check', items: ['Dirty air filter', 'Low refrigerant', 'Compressor issue', 'Thermostat problem'] },
          { kind: 'price', label: 'Estimated cost', value: '₹699 – ₹2499', sub: 'Duration: 1–2 hours' },
        ],
        suggestions: ['🏠 Book Now', '💰 Pricing', '📞 Support'],
      },
    );
  }

  if (intent.type === 'reset' || hasAnyIndicIntent(q, RESET_INTENTS)) {
    return resetReply('Okay. What would you like help with?', mainMenuActions());
  }
  if (intent.type === 'greeting' || hasAnyIndicIntent(q, GREETING_INTENTS)) {
    const welcome = getWelcomeMessage(activeCtx);
    return aiTurn(welcome.content, { ...EMPTY_SESSION }, welcome.actions, {
      rich: welcome.rich,
      suggestions: welcome.suggestions,
    });
  }
  if (intent.type === 'thanks' || hasAnyIndicIntent(q, THANKS_INTENTS)) {
    return resetReply("You're welcome. I am here whenever you need help.", mainMenuActions());
  }

  const quickCancel = await tryQuickCancelFromOrders(q, activeCtx, session);
  if (quickCancel) return quickCancel;

  const fulfilled = await fulfillSocIntent(intent, activeCtx, session);
  if (fulfilled) return fulfilled;

  if (isBareServiceCatalogQuery(q)) {
    return startBookingFlow(q, activeCtx);
  }

  const faq = scoreFaq(q);
  if (faq) {
    return aiReset(`**${faq.question}**\n\n${faq.answer}`, mainMenuActions());
  }

  const unclearHelp = tryUnclearMessageHelp(q, activeCtx);
  if (unclearHelp) return unclearHelp;

  return resetReply(t(languagePrefix(activeCtx), 'fallback'), mainMenuActions());
}
